import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/Integrations.css';
import shopifyLogo from '../../assets/images/shopify-logo.png';
import unicommerceLogo from '../../assets/images/unicommerce-logo.png';
import increffLogo from '../../assets/images/increff-logo.png';
import sapLogo from '../../assets/images/sap-logo.png';
import adobeCommerceLogo from '../../assets/images/adobe-commerce-logo.png';
import bigCommerceLogo from '../../assets/images/bigcommerce-logo.png';
import wooCommerceLogo from '../../assets/images/woocommerce-logo.png';
import easyecomLogo from '../../assets/images/easyecom.png';
import vinculumLogo from '../../assets/images/vinculum.png';
import { API_BASE_URL } from '../../config';

function Integrations({ email, onLogout }) {
  const navigate = useNavigate();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const initialIntegrations = useMemo(() => [
    {
      id: 1,
      name: 'Shopify',
      status: 'Planned',
      description: 'E-commerce platform for online stores',
      logo: shopifyLogo,
      lastSync: null,
      requiredFields: [
        { name: 'accessToken', label: 'Admin API Access Token', type: 'password', placeholder: 'Shopify Admin API Access Token' },
        { name: 'shopUrl', label: 'Shop URL', type: 'text', placeholder: 'your-store.myshopify.com' }
      ],
      requiredScopes: [
        'read_products',
        'write_products',
        'read_inventory',
        'write_inventory',
        'read_locations'
      ]
    },
    {
      id: 2,
      name: 'Unicommerce',
      status: 'Planned',
      description: 'Unified commerce platform for order management',
      logo: unicommerceLogo,
      lastSync: null,
      requiredFields: [
        { name: 'username', label: 'Username', type: 'text' },
        { name: 'password', label: 'Password', type: 'password' },
        { name: 'facilityCode', label: 'Facility Code', type: 'text' },
        { name: 'tenantCode', label: 'Tenant Code', type: 'text' },
        { name: 'baseUrl', label: 'Base URL', type: 'url', placeholder: 'https://api.unicommerce.com' }
      ]
    },
    {
      id: 3,
      name: 'Increff',
      status: 'Planned',
      description: 'Warehouse management and inventory optimization',
      logo: increffLogo,
      lastSync: null,
      requiredFields: [
        { name: 'apiKey', label: 'API Key', type: 'text' },
        { name: 'clientId', label: 'Client ID', type: 'text' },
        { name: 'warehouseId', label: 'Warehouse ID', type: 'text' },
        { name: 'baseUrl', label: 'Base URL', type: 'url', placeholder: 'https://api.increff.com' }
      ]
    },
    {
      id: 4,
      name: 'SAP',
      status: 'Planned',
      description: 'Enterprise resource planning solution',
      logo: sapLogo,
      lastSync: null,
      requiredFields: [
        { name: 'systemUrl', label: 'System URL', type: 'url' },
        { name: 'clientId', label: 'Client ID', type: 'text' },
        { name: 'username', label: 'Username', type: 'text' },
        { name: 'password', label: 'Password', type: 'password' },
        { name: 'companyDb', label: 'Company Database', type: 'text' },
        { name: 'warehouseCode', label: 'Warehouse Code', type: 'text' }
      ]
    },
    {
      id: 5,
      name: 'Magento',
      status: 'Planned',
      description: 'Adobe Enterprise e-commerce platform',
      logo: adobeCommerceLogo,
      lastSync: null,
      requiredFields: [
        { name: 'accessToken', label: 'Access Token', type: 'text' },
        { name: 'storeUrl', label: 'Store URL', type: 'url' },
        { name: 'storeCode', label: 'Store Code', type: 'text' },
        { name: 'sourceCode', label: 'Source/Warehouse Code', type: 'text' }
      ]
    },
    {
      id: 6,
      name: 'BigCommerce',
      status: 'Planned',
      description: 'SaaS e-commerce platform for growing businesses',
      logo: bigCommerceLogo,
      lastSync: null,
      requiredFields: [
        { name: 'storeHash', label: 'Store Hash', type: 'text' },
        { name: 'accessToken', label: 'Access Token', type: 'text' },
        { name: 'clientId', label: 'Client ID', type: 'text' },
        { name: 'clientSecret', label: 'Client Secret', type: 'password' }
      ]
    },
    {
      id: 7,
      name: 'WooCommerce',
      status: 'Planned',
      description: 'WordPress e-commerce plugin for online stores',
      logo: wooCommerceLogo,
      lastSync: null,
      requiredFields: [
        { name: 'consumerKey', label: 'Consumer Key', type: 'text' },
        { name: 'consumerSecret', label: 'Consumer Secret', type: 'password' },
        { name: 'storeUrl', label: 'Store URL', type: 'url' },
        { name: 'apiVersion', label: 'API Version', type: 'text', placeholder: 'wc/v3' }
      ]
    },
    {
      id: 8,
      name: 'Vinculum',
      status: 'Planned',
      description: 'Vinculum Integration for stores',
      logo: vinculumLogo,
      lastSync: null,
      requiredFields: [
        { name: 'clientId', label: 'Client ID', type: 'text' },
        { name: 'clientSecret', label: 'Client Secret', type: 'password' },
        { name: 'username', label: 'Username', type: 'text' },
        { name: 'password', label: 'Password', type: 'password' },
        { name: 'baseUrl', label: 'Base URL', type: 'url', placeholder: 'https://api.vinculum.com' }
      ]
    },
    {
      id: 9,
      name: 'EasyEcom',
      status: 'Planned',
      description: 'EasyEcom Integration for stores',
      logo: easyecomLogo,
      lastSync: null,
      requiredFields: [
        { name: 'apiKey', label: 'API Key', type: 'text' },
        { name: 'secretKey', label: 'Secret Key', type: 'password' },
        { name: 'sellerId', label: 'Seller ID', type: 'text' },
        { name: 'warehouseId', label: 'Warehouse ID', type: 'text' },
        { name: 'baseUrl', label: 'Base URL', type: 'url', placeholder: 'https://api.easyecom.io' }
      ]
    }
  ], []);

  const [integrationsData, setIntegrationsData] = useState(initialIntegrations);
  
  useEffect(() => {
    const fetchIntegrationStatuses = async () => {
      try {
        // Get auth credentials from localStorage
        const authKey = localStorage.getItem('authKey') || '';
        const userId = localStorage.getItem('userId') || '';
        
        const response = await fetch(`${API_BASE_URL}/brands/integrations/all`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            auth_key: authKey,
            user_id: userId
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.status === 200 && result.data) {
            // Update integrations with API data
            const updatedIntegrations = initialIntegrations.map(integration => {
              const platformData = result.data[integration.name.toLowerCase()];
              if (platformData) {
                return {
                  ...integration,
                  status: platformData.status === 'active' ? 'Pending' : platformData.status,
                  lastSync: new Date(platformData.lastSyncedAt).toLocaleString(),
                };
              }
              return integration;
            });
            setIntegrationsData(updatedIntegrations);
          }
        }
      } catch (err) {
        console.error('Failed to fetch integration statuses:', err);
      }
    };

    fetchIntegrationStatuses();
  }, [email, initialIntegrations]);

  const handleIntegrationClick = async (integration) => {
    setSelectedIntegration(integration);
    setFormData({});
    setError('');
    setSuccess('');
    setIsModalOpen(true);

    try {
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      // Fetch existing credentials - this endpoint expects a POST method with user_id and auth_key
      const response = await fetch(`${API_BASE_URL}/brands/integrations/${integration.name.toLowerCase().replace(/ /g, '_')}/fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          auth_key: authKey
        })
      });
      console.log(response);
      if (response.ok) {
        const result = await response.json();
        if (result.status === 200 && result.data && result.data.credentials) {
          setFormData(result.data.credentials);
        }
      }
    } catch (err) {
      console.log('No existing credentials found:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate all required fields are filled
    const missingFields = selectedIntegration.requiredFields.filter(
      field => !formData[field.name]
    );

    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    try {
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      // Platform specific payload preparation
      let credentials = {};
      switch (selectedIntegration.name.toLowerCase()) {
        case 'shopify':
          credentials = {
            accessToken: formData.accessToken,
            shopUrl: formData.shopUrl,
            platform: 'shopify',
            scopes: selectedIntegration.requiredScopes
          };
          break;
        case 'unicommerce':
          credentials = {
            username: formData.username,
            password: formData.password,
            facilityCode: formData.facilityCode,
            tenantCode: formData.tenantCode,
            baseUrl: formData.baseUrl,
            platform: 'unicommerce'
          };
          break;
        case 'increff':
          credentials = {
            apiKey: formData.apiKey,
            clientId: formData.clientId,
            warehouseId: formData.warehouseId,
            baseUrl: formData.baseUrl,
            platform: 'increff'
          };
          break;
        case 'sap':
          credentials = {
            systemUrl: formData.systemUrl,
            clientId: formData.clientId,
            username: formData.username,
            password: formData.password,
            companyDb: formData.companyDb,
            warehouseCode: formData.warehouseCode,
            platform: 'sap'
          };
          break;
        case 'magento':
          credentials = {
            accessToken: formData.accessToken,
            storeUrl: formData.storeUrl,
            storeCode: formData.storeCode,
            sourceCode: formData.sourceCode,
            platform: 'magento'
          };
          break;
        case 'bigcommerce':
          credentials = {
            storeHash: formData.storeHash,
            accessToken: formData.accessToken,
            clientId: formData.clientId,
            clientSecret: formData.clientSecret,
            platform: 'bigcommerce'
          };
          break;
        case 'woocommerce':
          credentials = {
            consumerKey: formData.consumerKey,
            consumerSecret: formData.consumerSecret,
            storeUrl: formData.storeUrl,
            apiVersion: formData.apiVersion,
            platform: 'woocommerce'
          };
          break;
        case 'vinculum':
          credentials = {
            clientId: formData.clientId,
            clientSecret: formData.clientSecret,
            username: formData.username,
            password: formData.password,
            baseUrl: formData.baseUrl,
            platform: 'vinculum'
          };
          break;
        case 'easyecom':
          credentials = {
            apiKey: formData.apiKey,
            secretKey: formData.secretKey,
            sellerId: formData.sellerId,
            warehouseId: formData.warehouseId,
            baseUrl: formData.baseUrl,
            platform: 'easyecom'
          };
          break;
        default:
          throw new Error('Unsupported platform');
      }

      // Prepare the payload in the exact format expected by the backend
      const finalPayload = {
        user_id: userId,
        auth_key: authKey,
        platform: selectedIntegration.name.toLowerCase(),
        status: 'active',
        lastSyncedAt: new Date().toISOString(),
        credentials: credentials
      };

      // Log the payload for testing
      console.log('Integration Payload:', JSON.stringify(finalPayload, null, 2));

      // Make the API call
      const response = await fetch(`${API_BASE_URL}/brands/integrations/${credentials.platform}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to connect: ${response.statusText}`);
      }

      await response.json();
      setSuccess(`Successfully saved credentials to ${selectedIntegration.name}!`);
      
      // Update the integration status locally
      const updatedIntegrations = integrationsData.map(integration => {
        if (integration.id === selectedIntegration.id) {
          return {
            ...integration,
            status: 'Pending',
            lastSync: new Date().toLocaleString()
          };
        }
        return integration;
      });
      setIntegrationsData(updatedIntegrations);
      setSelectedIntegration(prev => ({
        ...prev,
        status: 'Pending',
        lastSync: new Date().toLocaleString()
      }));

      // Close modal after delay
      setTimeout(() => {
        setIsModalOpen(false);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to connect to the platform');
    }
  };

  const handleVerifyConnection = async () => {
    setError('');
    setSuccess('');

    // Get auth credentials from localStorage
    const authKey = localStorage.getItem('authKey') || '';
    const userId = localStorage.getItem('userId') || '';

    const verifyPayload = {
      user_id: userId,
      auth_key: authKey,
      platform: selectedIntegration.name.toLowerCase()
    };

    // Log the payload for verification
    console.log('Verify Integration Payload:', JSON.stringify(verifyPayload, null, 2));

    try {
      const response = await fetch(`${API_BASE_URL}/brands/integrations/${selectedIntegration.name.toLowerCase()}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verifyPayload),
      });

      if (!response.ok) {
        throw new Error('Failed to verify connection');
      }

      const result = await response.json();
      if (result.status === 200) {
        setSuccess('Connection verified successfully!');
        // Update the integration status to "Available"
        const updatedIntegrations = integrationsData.map(integration => {
          if (integration.id === selectedIntegration.id) {
            return {
              ...integration,
              status: 'Available'
            };
          }
          return integration;
        });
        setIntegrationsData(updatedIntegrations);
      } else {
        setError(result.message || 'Failed to verify connection');
      }
    } catch (err) {
      setError(err.message || 'Failed to verify connection');
    }
  };

  const areAllFieldsFilled = () => {
    if (!selectedIntegration || !selectedIntegration.requiredFields) return false;
    return selectedIntegration.requiredFields.every(field => formData[field.name]?.trim());
  };

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Integrations" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="integrations-grid">
          {integrationsData.map((integration) => (
            <div 
              key={integration.id}
              className="integration-card"
              onClick={() => handleIntegrationClick(integration)}
            >
              <div className="integration-info">
                <div className="integration-header">
                  <div className="integration-logo">
                    <img src={integration.logo} alt={`${integration.name} logo`} />
                  </div>
                  <h3>{integration.name}</h3>
                  <div className={`status-badge ${integration.status.toLowerCase()}`}>
                    {integration.status}
                  </div>
                </div>
                <p className="integration-description">{integration.description}</p>
                {integration.lastSync && (
                  <p className="last-sync">Last synced: {integration.lastSync}</p>
                )}
              </div>
            </div>
          ))}

          <div 
            className="integration-card add-new"
            onClick={() => navigate('/integrations/add')}
          >
            <div className="add-integration-content">
              <div className="plus-icon">+</div>
              <h3>Add New Integration</h3>
              <p>Connect with more platforms and services</p>
            </div>
          </div>
        </div>

        {isModalOpen && selectedIntegration && (
          <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Connect to {selectedIntegration.name}</h3>
                <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
              </div>
              <div className="modal-body">
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}
                
                <form onSubmit={handleSubmit}>
                  {selectedIntegration.requiredFields.map((field) => (
                    <div className="form-group" key={field.name}>
                      <label htmlFor={field.name}>{field.label}*</label>
                      <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleInputChange}
                        required
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={!areAllFieldsFilled()}
                  >
                    Save Credentials
                  </button>
                  {selectedIntegration.status === 'Pending' && (
                    <button 
                      type="button" 
                      className="verify-btn"
                      onClick={handleVerifyConnection}
                    >
                      Verify Connection
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Integrations; 